import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import { errHandler } from '../../helper/helper';
import { amountWiseDisposeLoan } from './AuditService';
import { exportBranchWiseLoanExcel } from '../../Utils/exportBranchWiseLoanExcel';
const { Option } = Select;

const LoanAmountWiseDispose = ({ loginPlaceId, products, districtData }) => {
    const [filters, setFilters] = useState({
        districts: [],
        divisionalAreas: [],
        branches: [],
        products: [],
        date: null
    });

    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [loading, setLoading] = useState(false);

    // Update divisional areas when districts change
    useEffect(() => {
        const filteredDivisionalAreas = districtData
            .filter(d => filters.districts.includes(d.districtId))
            .flatMap(d => d.divisionalAreaDtoList || []);

        setFilters(prev => ({
            ...prev,
            divisionalAreas: prev.divisionalAreas.filter(did =>
                filteredDivisionalAreas.some(d => d.divAId === did)
            )
        }));

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [filters.districts, districtData]);

    // Update available branches based on selected districts
    useEffect(() => {
        const filtered = districtData
            .filter(d => filters.districts.includes(d.districtId))
            .flatMap(d => d.branchDtoList || []);

        setAvailableBranches(filtered);

        setFilters(prev => ({
            ...prev,
            branches: prev.branches.filter(bid =>
                filtered.some(b => b.id === bid)
            )
        }));
    }, [filters.districts, districtData]);

    // Update available branches based on selected divisional areas
    useEffect(() => {
        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => filters.divisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);

        setFilters(prev => ({
            ...prev,
            branches: prev.branches.filter(bid =>
                filteredBranches.some(b => b.id === bid)
            )
        }));
    }, [filters.divisionalAreas, districtData]);

     const isGenerateDisabled = !(
        filters.branches.length > 0 &&
        filters.products.length > 0 &&
        filters.date
    );


    const generateReport = () => {
        setLoading(true);
        const data = {
            branchIdList: filters.branches,
            productIdList: filters.products,
            selectedDate: filters.date ? filters.date.format('YYYY-MM-DD') : null
        };
        amountWiseDisposeLoan(data)
            .then(res => {
                exportBranchWiseLoanExcel(res?.data || [], filters.date || new Date());
                setLoading(false);
            })
            .catch(err => {
                errHandler(err);
                setLoading(false);
            });
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    {loginPlaceId === "3" && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={filters.districts}
                                        onChange={value =>
                                            setFilters(prev => ({ ...prev, districts: value }))
                                        }
                                    >
                                        {districtData.map(d => (
                                            <Option key={d.districtId} value={d.districtId}>
                                                🏙️ {d.districtName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {loginPlaceId === "3" && (
                        <Col span={24}>
                            <Form.Item label="Select Divisional Areas">
                                <Select
                                    mode="multiple"
                                    placeholder="Select divisional areas"
                                    style={{ width: '100%' }}
                                    value={filters.divisionalAreas}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            setFilters(prev => ({
                                                ...prev,
                                                divisionalAreas: availableDivisionalAreas.map(d => d.divAId)
                                            }));
                                        } else {
                                            setFilters(prev => ({ ...prev, divisionalAreas: values }));
                                        }
                                    }}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Divisional Areas
                                    </Option>
                                    {availableDivisionalAreas.map(d => (
                                        <Option key={d.divAId} value={d.divAId}>
                                            🏙️ {d.divAIdName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    {loginPlaceId === "3" && (
                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={filters.branches}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setFilters(prev => ({
                                                ...prev,
                                                branches: availableBranches.map(b => b.id)
                                            }));
                                        } else {
                                            setFilters(prev => ({ ...prev, branches: values }));
                                        }
                                    }}
                                    disabled={availableBranches.length === 0}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Branches
                                    </Option>
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    <Col span={24}>
                        <Form.Item label="Select Products">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select products"
                                style={{ width: '100%' }}
                                value={filters.products}
                                onChange={(values) => {
                                    if (values.includes('ALL')) {
                                        setFilters(prev => ({
                                            ...prev,
                                            products: products.map(b => b.productId)
                                        }));
                                    } else {
                                        setFilters(prev => ({ ...prev, products: values }));
                                    }
                                }}
                            >
                                <Option key="ALL" value="ALL">
                                    🔘 Select All Products
                                </Option>
                                {products.map(product => (
                                    <Option key={product.productId} value={product.productId}>
                                        {product.productName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Date">
                                <DatePicker
                                    value={filters.date}
                                    onChange={(date) =>
                                        setFilters(prev => ({ ...prev, date }))
                                    }
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={generateReport}
                                    disabled={loading || isGenerateDisabled}
                                    className="generate-btn"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default LoanAmountWiseDispose;
